import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { 
  Users, Clock, CheckCircle, XCircle, AlertCircle, MessageSquare,
  Eye, Check, X, Send, RefreshCw, ChevronDown, ChevronUp
} from 'lucide-react';
import { toast } from 'sonner';
import { BACKEND_URL } from '../config/api';

const API = `${BACKEND_URL}/api`;

export default function ParentApprovalDashboard() {
  const [requests, setRequests] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [infoMessage, setInfoMessage] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    loadData();
  }, [filter]);

  const loadData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const [requestsRes, statsRes] = await Promise.all([
        axios.get(`${API}/parent-linking/admin/${filter === 'all' ? 'all' : 'pending'}`, {
          params: filter !== 'all' && filter !== 'pending' ? { status: filter } : {},
          headers
        }),
        axios.get(`${API}/parent-linking/admin/stats`, { headers })
      ]);
      
      setRequests(requestsRes.data.requests);
      setStats(statsRes.data);
    } catch (err) {
      console.error('Failed to load data:', err);
      toast.error('Failed to load approval requests');
    } finally {
      setLoading(false);
    }
  };

  const viewRequestDetails = async (request) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/parent-linking/admin/request/${request.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedRequest(response.data);
    } catch (err) {
      toast.error('Failed to load request details');
    }
  };

  const handleApprove = async (requestId) => {
    setActionLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/parent-linking/admin/approve`, {
        request_id: requestId,
        action: 'approve'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Request approved! Parent has been notified.');
      setSelectedRequest(null);
      loadData();
    } catch (err) {
      toast.error('Failed to approve request');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async (requestId) => {
    if (!rejectionReason.trim()) {
      toast.error('Please provide a rejection reason');
      return;
    }
    
    setActionLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/parent-linking/admin/approve`, {
        request_id: requestId,
        action: 'reject',
        rejection_reason: rejectionReason
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Request rejected. Parent has been notified.');
      setSelectedRequest(null);
      setRejectionReason('');
      loadData();
    } catch (err) {
      toast.error('Failed to reject request');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRequestInfo = async (requestId) => {
    if (!infoMessage.trim()) {
      toast.error('Please enter a message');
      return;
    }
    
    setActionLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/parent-linking/admin/request-info`, {
        request_id: requestId,
        message: infoMessage
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Information request sent to parent');
      setShowInfoModal(false);
      setInfoMessage('');
      loadData();
    } catch (err) {
      toast.error('Failed to send information request');
    } finally {
      setActionLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-500/20 text-yellow-400',
      info_requested: 'bg-blue-500/20 text-blue-400',
      approved: 'bg-green-500/20 text-green-400',
      rejected: 'bg-red-500/20 text-red-400'
    };
    
    const labels = {
      pending: 'Pending',
      info_requested: 'Info Requested',
      approved: 'Approved',
      rejected: 'Rejected'
    };
    
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status] || styles.pending}`}>
        {labels[status] || status}
      </span>
    );
  };

  const getRelationshipLabel = (rel) => {
    const labels = {
      mother: 'Mother',
      father: 'Father',
      guardian: 'Legal Guardian',
      grandparent: 'Grandparent',
      step_parent: 'Step Parent',
      other: 'Other'
    };
    return labels[rel] || rel;
  };

  return (
    <div className="space-y-6" data-testid="parent-approval-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center gap-3">
            <Users className="h-7 w-7 text-[#02b5ff]" />
            Parent Registration Approvals
          </h2>
          <p className="text-zinc-400 text-sm mt-1">Review and approve parent account requests</p>
        </div>
        <Button
          variant="outline"
          onClick={loadData}
          className="border-zinc-700 text-zinc-300 hover:text-white"
        >
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div 
            className={`bg-zinc-900/50 border rounded-xl p-4 cursor-pointer transition-all ${
              filter === 'pending' ? 'border-yellow-500' : 'border-zinc-800 hover:border-zinc-600'
            }`}
            onClick={() => setFilter('pending')}
          >
            <div className="flex items-center gap-3">
              <Clock className="h-5 w-5 text-yellow-500" />
              <div>
                <p className="text-2xl font-bold text-white">{stats.pending}</p>
                <p className="text-xs text-zinc-500">Pending</p>
              </div>
            </div>
          </div>
          
          <div 
            className={`bg-zinc-900/50 border rounded-xl p-4 cursor-pointer transition-all ${
              filter === 'info_requested' ? 'border-blue-500' : 'border-zinc-800 hover:border-zinc-600'
            }`}
            onClick={() => setFilter('info_requested')}
          >
            <div className="flex items-center gap-3">
              <AlertCircle className="h-5 w-5 text-blue-500" />
              <div>
                <p className="text-2xl font-bold text-white">{stats.info_requested}</p>
                <p className="text-xs text-zinc-500">Info Requested</p>
              </div>
            </div>
          </div>
          
          <div 
            className={`bg-zinc-900/50 border rounded-xl p-4 cursor-pointer transition-all ${
              filter === 'approved' ? 'border-green-500' : 'border-zinc-800 hover:border-zinc-600'
            }`}
            onClick={() => setFilter('approved')}
          >
            <div className="flex items-center gap-3">
              <CheckCircle className="h-5 w-5 text-green-500" />
              <div>
                <p className="text-2xl font-bold text-white">{stats.approved}</p>
                <p className="text-xs text-zinc-500">Approved</p>
              </div>
            </div>
          </div>
          
          <div 
            className={`bg-zinc-900/50 border rounded-xl p-4 cursor-pointer transition-all ${
              filter === 'rejected' ? 'border-red-500' : 'border-zinc-800 hover:border-zinc-600'
            }`}
            onClick={() => setFilter('rejected')}
          >
            <div className="flex items-center gap-3">
              <XCircle className="h-5 w-5 text-red-500" />
              <div>
                <p className="text-2xl font-bold text-white">{stats.rejected}</p>
                <p className="text-xs text-zinc-500">Rejected</p>
              </div>
            </div>
          </div>
          
          <div 
            className={`bg-zinc-900/50 border rounded-xl p-4 cursor-pointer transition-all ${
              filter === 'all' ? 'border-[#02b5ff]' : 'border-zinc-800 hover:border-zinc-600'
            }`}
            onClick={() => setFilter('all')}
          >
            <div className="flex items-center gap-3">
              <Users className="h-5 w-5 text-[#02b5ff]" />
              <div>
                <p className="text-2xl font-bold text-white">{stats.total}</p>
                <p className="text-xs text-zinc-500">All Time</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Requests List */}
      <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl overflow-hidden">
        <div className="p-4 border-b border-zinc-800">
          <h3 className="text-white font-semibold">
            {filter === 'all' ? 'All Requests' : `${filter.charAt(0).toUpperCase() + filter.slice(1).replace('_', ' ')} Requests`}
          </h3>
        </div>
        
        {loading ? (
          <div className="p-8 text-center">
            <div className="text-zinc-500 animate-pulse">Loading...</div>
          </div>
        ) : requests.length === 0 ? (
          <div className="p-8 text-center">
            <Users className="h-12 w-12 text-zinc-700 mx-auto mb-3" />
            <p className="text-zinc-500">No requests found</p>
          </div>
        ) : (
          <div className="divide-y divide-zinc-800">
            {requests.map((request) => (
              <div
                key={request.id}
                className="p-4 hover:bg-zinc-800/30 transition-colors"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <p className="text-white font-medium">
                        {request.parent_first_name} {request.parent_last_name}
                      </p>
                      {getStatusBadge(request.status)}
                    </div>
                    <p className="text-sm text-zinc-400">
                      Requesting link to: <span className="text-zinc-300">{request.child_name}</span>
                    </p>
                    <p className="text-xs text-zinc-500 mt-1">
                      {getRelationshipLabel(request.relationship)} • {request.email} • {request.phone}
                    </p>
                    <p className="text-xs text-zinc-600 mt-1">
                      Submitted: {new Date(request.created_at).toLocaleString()}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => viewRequestDetails(request)}
                      className="text-zinc-400 hover:text-white"
                      data-testid={`view-request-${request.id}`}
                    >
                      <Eye className="h-4 w-4" />
                    </Button>
                    {request.status === 'pending' && (
                      <>
                        <Button
                          size="sm"
                          onClick={() => handleApprove(request.id)}
                          className="bg-green-600 hover:bg-green-500 text-white"
                          data-testid={`approve-${request.id}`}
                        >
                          <Check className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => {
                            setSelectedRequest({ request });
                            setRejectionReason('');
                          }}
                          data-testid={`reject-${request.id}`}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setSelectedRequest({ request });
                            setShowInfoModal(true);
                          }}
                          className="border-zinc-700 text-zinc-300 hover:text-white"
                          data-testid={`request-info-${request.id}`}
                        >
                          <MessageSquare className="h-4 w-4" />
                        </Button>
                      </>
                    )}
                  </div>
                </div>
                
                {/* Additional info responses */}
                {request.additional_info_responses?.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-zinc-800">
                    <p className="text-xs text-zinc-500 mb-2">Additional Info Received:</p>
                    {request.additional_info_responses.map((response, idx) => (
                      <div key={idx} className="bg-zinc-800/50 rounded p-2 text-xs text-zinc-300">
                        {response.message || JSON.stringify(response.data)}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Request Details Modal */}
      {selectedRequest && !showInfoModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl max-w-lg w-full max-h-[80vh] overflow-y-auto">
            <div className="p-6 border-b border-zinc-800">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold text-white">Request Details</h3>
                <button
                  onClick={() => setSelectedRequest(null)}
                  className="text-zinc-400 hover:text-white"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
            </div>
            
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-zinc-500 uppercase tracking-wider">Parent Name</p>
                  <p className="text-white font-medium">
                    {selectedRequest.request?.parent_first_name} {selectedRequest.request?.parent_last_name}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-zinc-500 uppercase tracking-wider">Relationship</p>
                  <p className="text-white">{getRelationshipLabel(selectedRequest.request?.relationship)}</p>
                </div>
                <div>
                  <p className="text-xs text-zinc-500 uppercase tracking-wider">Email</p>
                  <p className="text-white">{selectedRequest.request?.email}</p>
                </div>
                <div>
                  <p className="text-xs text-zinc-500 uppercase tracking-wider">Phone</p>
                  <p className="text-white">{selectedRequest.request?.phone}</p>
                </div>
              </div>
              
              {selectedRequest.child && (
                <div className="bg-zinc-800/50 rounded-lg p-4">
                  <p className="text-xs text-zinc-500 uppercase tracking-wider mb-2">Child Information</p>
                  <p className="text-white font-medium">
                    {selectedRequest.child.first_name} {selectedRequest.child.last_name}
                  </p>
                  {selectedRequest.child.date_of_birth && (
                    <p className="text-sm text-zinc-400">
                      DOB: {new Date(selectedRequest.child.date_of_birth).toLocaleDateString()}
                    </p>
                  )}
                  {selectedRequest.child.current_team_name && (
                    <p className="text-sm text-zinc-400">Team: {selectedRequest.child.current_team_name}</p>
                  )}
                </div>
              )}
              
              {selectedRequest.existing_links?.length > 0 && (
                <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
                  <p className="text-xs text-yellow-500 uppercase tracking-wider mb-2">Existing Links</p>
                  {selectedRequest.existing_links.map((link, idx) => (
                    <p key={idx} className="text-sm text-zinc-300">
                      {link.parent_email} ({link.relationship})
                    </p>
                  ))}
                </div>
              )}
              
              {selectedRequest.request?.notes && (
                <div>
                  <p className="text-xs text-zinc-500 uppercase tracking-wider mb-1">Notes</p>
                  <p className="text-zinc-300 text-sm">{selectedRequest.request.notes}</p>
                </div>
              )}
              
              {/* Rejection reason input */}
              {selectedRequest.request?.status === 'pending' && (
                <div className="space-y-3 pt-4 border-t border-zinc-800">
                  <div>
                    <label className="text-xs text-zinc-500 uppercase tracking-wider">
                      Rejection Reason (required for rejection)
                    </label>
                    <textarea
                      value={rejectionReason}
                      onChange={(e) => setRejectionReason(e.target.value)}
                      className="w-full mt-2 bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 text-white focus:border-[#02b5ff] focus:outline-none resize-none h-20"
                      placeholder="Enter reason if rejecting..."
                    />
                  </div>
                  
                  <div className="flex gap-3">
                    <Button
                      onClick={() => handleApprove(selectedRequest.request.id)}
                      disabled={actionLoading}
                      className="flex-1 bg-green-600 hover:bg-green-500 text-white"
                    >
                      <Check className="h-4 w-4 mr-2" />
                      Approve
                    </Button>
                    <Button
                      onClick={() => handleReject(selectedRequest.request.id)}
                      disabled={actionLoading || !rejectionReason.trim()}
                      variant="destructive"
                      className="flex-1"
                    >
                      <X className="h-4 w-4 mr-2" />
                      Reject
                    </Button>
                    <Button
                      onClick={() => setShowInfoModal(true)}
                      variant="outline"
                      className="border-zinc-700 text-zinc-300 hover:text-white"
                    >
                      <MessageSquare className="h-4 w-4 mr-2" />
                      Request Info
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Request Info Modal */}
      {showInfoModal && selectedRequest && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl max-w-md w-full">
            <div className="p-6 border-b border-zinc-800">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold text-white">Request Additional Information</h3>
                <button
                  onClick={() => {
                    setShowInfoModal(false);
                    setInfoMessage('');
                  }}
                  className="text-zinc-400 hover:text-white"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
            </div>
            
            <div className="p-6 space-y-4">
              <div>
                <p className="text-sm text-zinc-400 mb-2">
                  Send a message to <span className="text-white">{selectedRequest.request?.parent_first_name}</span> requesting additional information.
                </p>
                <textarea
                  value={infoMessage}
                  onChange={(e) => setInfoMessage(e.target.value)}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 text-white focus:border-[#02b5ff] focus:outline-none resize-none h-32"
                  placeholder="e.g., Please provide a copy of your ID for verification..."
                  data-testid="info-request-message"
                />
              </div>
              
              <div className="flex gap-3">
                <Button
                  variant="ghost"
                  onClick={() => {
                    setShowInfoModal(false);
                    setInfoMessage('');
                  }}
                  className="flex-1 text-zinc-400"
                >
                  Cancel
                </Button>
                <Button
                  onClick={() => handleRequestInfo(selectedRequest.request.id)}
                  disabled={actionLoading || !infoMessage.trim()}
                  className="flex-1 bg-[#02b5ff] hover:bg-[#02b5ff]/90 text-white"
                >
                  <Send className="h-4 w-4 mr-2" />
                  Send Request
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
